/** Show, hide, reorder, and default top navigation tabs (web + mobile). */

import { normalizeTabId } from "./agenticFeatures";

export interface TabPrefDefinition {
  id: string;
  label: string;
  /** Always shown and cannot be hidden (e.g. Settings on web). */
  locked?: boolean;
}

export interface TabPreferences {
  order: string[];
  hidden: string[];
  defaultTab: string;
}

/** Stored in `defaultTab` to reopen the last active tab on launch/refresh. */
export const LAST_USED_TAB_PREF = "__last__";

/** Small crescent + star for the Astro tab strip label (currentColor). */
export const ASTRO_TAB_ICON_HTML = `<svg class="sfe-tab-astro-icon" viewBox="0 0 16 16" width="12" height="12" aria-hidden="true" focusable="false"><path fill="currentColor" d="M10.9 1.4a6.6 6.6 0 1 0 3.5 12.1A7.4 7.4 0 1 1 10.9 1.4z"/><path fill="currentColor" d="M12.55 2.85l.42 1.05 1.1.12-.85.72.28 1.07-.95-.58-.95.58.28-1.07-.85-.72 1.1-.12z"/></svg>`;

/** Prefix Astro tab labels with the strip icon. `labelHtml` must already be escaped. */
export function withAstroTabIcon(tabId: string, labelHtml: string): string {
  if (tabId !== "astrology") return labelHtml;
  return `<span class="sfe-tab-astro-label">${ASTRO_TAB_ICON_HTML}${labelHtml}</span>`;
}

const WEB_STORAGE_KEY = "sfe-web-tab-preferences-v1";
const MOBILE_STORAGE_KEY = "sfe-mobile-tab-preferences-v1";
const WEB_LAST_TAB_KEY = "sfe-web-last-tab-v1";
const MOBILE_LAST_TAB_KEY = "sfe-mobile-last-tab-v1";
const MODAL_ID = "sfe-tab-preferences-modal";

/**
 * Retired tabs → replacement (saved prefs / last-used tab).
 * Focus, History-SF, Calls+, Analytics-SF are gone forever from SF+.
 */
const RETIRED_WEB_TAB_ALIASES: Record<string, string> = {
  focus: "options",
  history: "history-local",
  "history-sf": "history-local",
  "calls-plus": "calls",
  analytics: "analytics-local",
  "analytics-sf": "analytics-local",
  "wheel-lab": "analytics-local",
};

/** Mobile Focus+ / experiment aliases → Options. */
const RETIRED_MOBILE_TAB_ALIASES: Record<string, string> = {
  focus: "options3",
  focus1: "options3",
  focus2: "options3",
  focus3: "options3",
  focus4: "options3",
  callsplus: "calls",
};

/** Map retired web tab ids (Focus, History-SF, Calls+, Analytics-SF, …) to live tabs. */
export function migrateWebTabId(id: string): string {
  return RETIRED_WEB_TAB_ALIASES[normalizeTabId(id)] ?? normalizeTabId(id);
}

/** Map retired mobile tab ids (Focus / Focus+, Calls+) to live tabs. */
export function migrateMobileTabId(id: string): string {
  return RETIRED_MOBILE_TAB_ALIASES[normalizeTabId(id)] ?? normalizeTabId(id);
}

export const WEB_TAB_PREF_DEFINITIONS: TabPrefDefinition[] = [
  { id: "puts", label: "Puts" },
  { id: "calls", label: "Calls" },
  { id: "options", label: "Options" },
  { id: "stocks", label: "Stocks" },
  { id: "history-local", label: "History" },
  { id: "analytics-local", label: "Analytics" },
  { id: "pfolio", label: "Pfolio" },
  { id: "market", label: "Market" },
  { id: "astrology", label: "Astro" },
  { id: "tax", label: "Tax" },
  { id: "import", label: "Import" },
  { id: "agents", label: "Agentic" },
];

export const MOBILE_TAB_PREF_DEFINITIONS: TabPrefDefinition[] = [
  { id: "options3", label: "Options" },
  { id: "puts", label: "Puts" },
  { id: "calls", label: "Calls" },
  { id: "stocks", label: "Stocks" },
  { id: "analytics", label: "Analytics" },
  { id: "pfolio", label: "Pfolio" },
  { id: "market", label: "Market" },
  { id: "astrology", label: "Astro" },
  { id: "tax", label: "Tax" },
  { id: "history", label: "History" },
  { id: "import", label: "Import" },
  { id: "agents", label: "Agentic" },
];

function defaultPreferences(definitions: TabPrefDefinition[]): TabPreferences {
  return {
    order: definitions.map((tab) => tab.id),
    hidden: [],
    defaultTab: definitions[0]?.id ?? "",
  };
}

function definitionMap(definitions: TabPrefDefinition[]): Map<string, TabPrefDefinition> {
  return new Map(definitions.map((tab) => [tab.id, tab]));
}

function esc(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

function ensureSettingsLast(order: string[]): string[] {
  if (!order.includes("settings")) return order;
  return [...order.filter((id) => id !== "settings"), "settings"];
}

function loadPreferences(
  storageKey: string,
  definitions: TabPrefDefinition[],
  migrateId: (id: string) => string = (id) => id,
): TabPreferences {
  const defaults = defaultPreferences(definitions);
  const byId = definitionMap(definitions);
  const normalize = (id: string) => migrateId(normalizeTabId(id));
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return defaults;
    const saved = JSON.parse(raw) as Partial<TabPreferences>;
    if (!saved || typeof saved !== "object") return defaults;

    const hiddenRaw = new Set(
      Array.isArray(saved.hidden)
        ? saved.hidden.filter((id): id is string => typeof id === "string")
        : [],
    );
    /** Replacement tabs that must stay visible because a retired source tab was visible. */
    const unhideFromRetired = new Set<string>();

    const order: string[] = [];
    const seen = new Set<string>();
    if (Array.isArray(saved.order)) {
      for (const rawId of saved.order) {
        if (typeof rawId !== "string") continue;
        const canonical = normalizeTabId(rawId);
        const id = migrateId(canonical);
        if (id !== canonical && !hiddenRaw.has(rawId) && !hiddenRaw.has(canonical)) {
          // e.g. Focus+ was shown while Options was hidden → keep Options after Focus+ retires
          unhideFromRetired.add(id);
        }
        if (!byId.has(id) || seen.has(id)) continue;
        seen.add(id);
        order.push(id);
      }
    }
    for (const tab of definitions) {
      if (!seen.has(tab.id)) {
        seen.add(tab.id);
        order.push(tab.id);
      }
    }

    const hidden = new Set<string>();
    if (Array.isArray(saved.hidden)) {
      for (const rawId of saved.hidden) {
        if (typeof rawId !== "string") continue;
        const id = normalize(rawId);
        if (byId.has(id)) hidden.add(id);
      }
    }
    for (const id of unhideFromRetired) {
      hidden.delete(id);
    }
    for (const tab of definitions) {
      if (tab.locked) hidden.delete(tab.id);
    }

    const visibleIds = order.filter((id) => !hidden.has(id));
    let defaultTab =
      saved.defaultTab === LAST_USED_TAB_PREF
        ? LAST_USED_TAB_PREF
        : typeof saved.defaultTab === "string" && byId.has(normalize(saved.defaultTab))
          ? normalize(saved.defaultTab)
          : defaults.defaultTab;
    if (defaultTab !== LAST_USED_TAB_PREF && !visibleIds.includes(defaultTab)) {
      defaultTab = visibleIds[0] ?? defaults.defaultTab;
    }

    const prefs = { order: ensureSettingsLast(order), hidden: [...hidden], defaultTab };
    // Persist migration so Options stays visible even after retired ids leave `order`.
    try {
      const prev = JSON.stringify(saved);
      const next = JSON.stringify(prefs);
      if (prev !== next) localStorage.setItem(storageKey, next);
    } catch {
      /* ignore quota / private mode */
    }
    return prefs;
  } catch {
    return defaults;
  }
}

function savePreferences(storageKey: string, prefs: TabPreferences): void {
  localStorage.setItem(storageKey, JSON.stringify(prefs));
}

export function loadWebTabPreferences(): TabPreferences {
  return loadPreferences(WEB_STORAGE_KEY, WEB_TAB_PREF_DEFINITIONS, migrateWebTabId);
}

export function saveWebTabPreferences(prefs: TabPreferences): void {
  savePreferences(WEB_STORAGE_KEY, prefs);
}

const MOBILE_RESTORE_OPTIONS_AFTER_FOCUS_KEY = "sfe-mobile-unhide-options-after-focus-retire-v1";

export function loadMobileTabPreferences(): TabPreferences {
  const prefs = loadPreferences(MOBILE_STORAGE_KEY, MOBILE_TAB_PREF_DEFINITIONS, migrateMobileTabId);
  // One-time: Focus+ removal could leave Options hidden for users who preferred Focus+.
  try {
    if (localStorage.getItem(MOBILE_RESTORE_OPTIONS_AFTER_FOCUS_KEY) !== "1") {
      localStorage.setItem(MOBILE_RESTORE_OPTIONS_AFTER_FOCUS_KEY, "1");
      if (prefs.hidden.includes("options3")) {
        const next = {
          ...prefs,
          hidden: prefs.hidden.filter((id) => id !== "options3"),
        };
        savePreferences(MOBILE_STORAGE_KEY, next);
        return next;
      }
    }
  } catch {
    /* ignore */
  }
  return prefs;
}

export function saveMobileTabPreferences(prefs: TabPreferences): void {
  savePreferences(MOBILE_STORAGE_KEY, prefs);
}

function loadLastTab(
  storageKey: string,
  migrateId: (id: string) => string = (id) => id,
): string | null {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw || typeof raw !== "string") return null;
    return migrateId(normalizeTabId(raw));
  } catch {
    return null;
  }
}

function saveLastTab(
  storageKey: string,
  tabId: string,
  migrateId: (id: string) => string = (id) => id,
): void {
  try {
    localStorage.setItem(storageKey, migrateId(normalizeTabId(tabId)));
  } catch {
    /* ignore quota / private mode */
  }
}

export function loadWebLastTab(): string | null {
  return loadLastTab(WEB_LAST_TAB_KEY, migrateWebTabId);
}

export function saveWebLastTab(tabId: string): void {
  saveLastTab(WEB_LAST_TAB_KEY, tabId, migrateWebTabId);
}

export function loadMobileLastTab(): string | null {
  return loadLastTab(MOBILE_LAST_TAB_KEY, migrateMobileTabId);
}

export function saveMobileLastTab(tabId: string): void {
  saveLastTab(MOBILE_LAST_TAB_KEY, tabId, migrateMobileTabId);
}

export function orderedVisibleTabIds(
  definitions: TabPrefDefinition[],
  prefs: TabPreferences,
): string[] {
  const byId = definitionMap(definitions);
  const hidden = new Set(prefs.hidden);
  return prefs.order.filter((id) => byId.has(id) && (!hidden.has(id) || byId.get(id)?.locked));
}

export function resolveDefaultTabId(
  definitions: TabPrefDefinition[],
  prefs: TabPreferences,
  lastTabId?: string | null,
): string {
  const visible = orderedVisibleTabIds(definitions, prefs);
  if (prefs.defaultTab === LAST_USED_TAB_PREF) {
    // Callers should pass an already-migrated last tab (loadWeb/MobileLastTab).
    const last = typeof lastTabId === "string" ? normalizeTabId(lastTabId) : lastTabId ?? null;
    if (last && visible.includes(last)) return last;
    return visible[0] ?? definitions[0]?.id ?? "";
  }
  if (visible.includes(prefs.defaultTab)) return prefs.defaultTab;
  return visible[0] ?? definitions[0]?.id ?? "";
}

export function isTabVisible(
  definitions: TabPrefDefinition[],
  prefs: TabPreferences,
  tabId: string,
): boolean {
  const tab = definitionMap(definitions).get(tabId);
  if (!tab) return false;
  if (tab.locked) return true;
  return !prefs.hidden.includes(tabId);
}

interface TabPrefDraftEntry extends TabPrefDefinition {
  visible: boolean;
}

export type { TabPrefDraftEntry };

const SECTION_BOUND = "data-sfe-tab-prefs-bound";

let activeDraft: TabPrefDraftEntry[] = [];
let activeDefaultTab = "";
let activePlatform: "web" | "mobile" = "web";
let activeDefinitions: TabPrefDefinition[] = [];
let activeOnApply: ((prefs: TabPreferences) => void) | null = null;
let modalBound = false;

let mountedSection: {
  host: HTMLElement;
  draft: TabPrefDraftEntry[];
  defaultTab: string;
  definitions: TabPrefDefinition[];
  platform: "web" | "mobile";
  save: (prefs: TabPreferences) => void;
  onApply: (prefs: TabPreferences) => void;
} | null = null;

export function buildTabPreferencesDraft(
  definitions: TabPrefDefinition[],
  prefs: TabPreferences,
): TabPrefDraftEntry[] {
  return buildDraft(definitions, prefs);
}

export function draftTabPreferences(
  draft: TabPrefDraftEntry[],
  defaultTab: string,
): TabPreferences {
  return draftToPreferences(draft, defaultTab);
}

export function moveTabPrefDraftEntry(
  draft: TabPrefDraftEntry[],
  index: number,
  direction: "up" | "down",
): TabPrefDraftEntry[] {
  return moveDraftEntry(draft, index, direction);
}

export function toggleTabPrefDraftEntry(
  draft: TabPrefDraftEntry[],
  id: string,
  visible: boolean,
): TabPrefDraftEntry[] {
  return toggleDraftEntry(draft, id, visible);
}

function buildDraft(definitions: TabPrefDefinition[], prefs: TabPreferences): TabPrefDraftEntry[] {
  const byId = definitionMap(definitions);
  const hidden = new Set(prefs.hidden);
  return prefs.order
    .filter((id) => byId.has(id))
    .map((id) => {
      const tab = byId.get(id)!;
      return {
        ...tab,
        visible: tab.locked === true || !hidden.has(id),
      };
    });
}

function draftToPreferences(draft: TabPrefDraftEntry[], defaultTab: string): TabPreferences {
  const order = ensureSettingsLast(draft.map((tab) => tab.id));
  const hidden = draft.filter((tab) => !tab.visible && !tab.locked).map((tab) => tab.id);
  const visible = draft.filter((tab) => tab.visible || tab.locked).map((tab) => tab.id);
  let nextDefault = defaultTab;
  if (nextDefault !== LAST_USED_TAB_PREF && !visible.includes(nextDefault)) {
    nextDefault = visible[0] ?? draft[0]?.id ?? "";
  }
  return { order, hidden, defaultTab: nextDefault };
}

function renderTabPreferenceRows(draft: TabPrefDraftEntry[], compact: boolean): string {
  return draft
    .map((tab, index) => {
      const locked = tab.locked === true;
      const orderButtons = locked
        ? compact
          ? `<span class="sfe-mobile-options3-column-fixed">Fixed</span>`
          : `<em class="sfe-column-settings-fixed">Fixed</em>`
        : `<button type="button" class="small ghost" data-action="tab-up" data-index="${index}" ${index <= 0 || draft[index - 1]?.locked ? "disabled" : ""} aria-label="Move ${esc(tab.label)} up">↑</button>
           <button type="button" class="small ghost" data-action="tab-down" data-index="${index}" ${index >= draft.length - 1 || draft[index + 1]?.locked ? "disabled" : ""} aria-label="Move ${esc(tab.label)} down">↓</button>`;

      if (compact) {
        return `
        <div class="sfe-mobile-options3-column-row sfe-mobile-tab-pref-row${locked ? " sfe-mobile-options3-column-row-locked" : ""}">
          <label><input type="checkbox" data-tab-toggle="${esc(tab.id)}" ${tab.visible ? "checked" : ""} ${locked ? "disabled" : ""} aria-label="${esc(tab.label)}" /><span>${esc(tab.label)}</span></label>
          <span class="sfe-mobile-options3-column-order">${orderButtons}</span>
        </div>`;
      }

      return `
        <div class="sfe-column-settings-row sfe-tab-prefs-row${locked ? " sfe-column-settings-row-locked" : ""}">
          <label class="sfe-column-settings-check">
            <input type="checkbox" data-tab-toggle="${esc(tab.id)}" ${tab.visible ? "checked" : ""} ${locked ? "disabled" : ""} aria-label="${esc(tab.label)}" />
          </label>
          <span class="sfe-column-settings-name">${esc(tab.label)}</span>
          <span class="sfe-column-settings-order">${orderButtons}</span>
        </div>`;
    })
    .join("");
}

function renderDefaultTabOptions(draft: TabPrefDraftEntry[], defaultTab: string): string {
  const lastUsed = `<option value="${LAST_USED_TAB_PREF}"${defaultTab === LAST_USED_TAB_PREF ? " selected" : ""}>Last used tab</option>`;
  const tabs = draft
    .filter((tab) => tab.visible || tab.locked)
    .map(
      (tab) =>
        `<option value="${esc(tab.id)}"${tab.id === defaultTab ? " selected" : ""}>${esc(tab.label)}</option>`,
    )
    .join("");
  return `${lastUsed}${tabs}`;
}

export function renderDefaultTabSelectOptions(
  draft: TabPrefDraftEntry[],
  defaultTab: string,
): string {
  return renderDefaultTabOptions(draft, defaultTab);
}

function renderTabPreferencesBody(
  draft: TabPrefDraftEntry[],
  defaultTab: string,
  compact = false,
): string {
  const defaultOptions = renderDefaultTabOptions(draft, defaultTab);
  const rows = renderTabPreferenceRows(draft, compact);
  const defaultRow = compact
    ? `<div class="sfe-mobile-tab-pref-default-row">
        <label for="sfe-mobile-tab-pref-default">Default tab</label>
        <select id="sfe-mobile-tab-pref-default" data-tab-default class="sfe-mobile-tab-pref-default-select">${defaultOptions}</select>
      </div>`
    : `<div class="sfe-column-settings-row sfe-tab-pref-default-row">
        <span class="sfe-column-settings-name">Default tab</span>
        <select data-tab-default class="sfe-tab-pref-default-select">${defaultOptions}</select>
      </div>`;

  const listClass = compact
    ? "sfe-mobile-options3-column-list sfe-mobile-tab-prefs-list"
    : "sfe-column-settings-list";

  return `
    ${defaultRow}
    <div class="${listClass}" data-tab-preferences-list>${rows}</div>`;
}

export function renderMobileTabPreferencesSection(
  draft: TabPrefDraftEntry[],
  defaultTab: string,
): string {
  return `
    <h4 class="wheel-settings-section-title sfe-web-settings-card-title">Navigation tabs</h4>
    <p class="muted small sfe-column-settings-lead">Show, hide, and reorder top navigation tabs. Pick which tab opens on launch, or resume the last tab you used.</p>
    <div data-tab-preferences-body>${renderTabPreferencesBody(draft, defaultTab, true)}</div>`;
}

function resolveDefaultTabForDraft(draft: TabPrefDraftEntry[], defaultTab: string): string {
  if (defaultTab === LAST_USED_TAB_PREF) return LAST_USED_TAB_PREF;
  const visible = draft.filter((tab) => tab.visible || tab.locked).map((tab) => tab.id);
  if (visible.includes(defaultTab)) return defaultTab;
  return visible[0] ?? draft[0]?.id ?? defaultTab;
}

function syncDefaultTabSelect(
  host: HTMLElement,
  draft: TabPrefDraftEntry[],
  defaultTab: string,
): string {
  const select = host.querySelector<HTMLSelectElement>("[data-tab-default]");
  if (!select) return resolveDefaultTabForDraft(draft, defaultTab);
  const nextDefault = resolveDefaultTabForDraft(draft, defaultTab);
  select.innerHTML = renderDefaultTabOptions(draft, nextDefault);
  select.value = nextDefault;
  return nextDefault;
}

function paintTabPreferencesList(
  host: HTMLElement,
  draft: TabPrefDraftEntry[],
  compact: boolean,
): void {
  const list = host.querySelector<HTMLElement>("[data-tab-preferences-list]");
  if (!list) return;
  const scrollTop = list.scrollTop;
  list.innerHTML = renderTabPreferenceRows(draft, compact);
  list.scrollTop = scrollTop;
}

function paintTabPreferencesBody(
  host: HTMLElement,
  draft: TabPrefDraftEntry[],
  defaultTab: string,
  compact: boolean,
): void {
  const body = host.querySelector<HTMLElement>("[data-tab-preferences-body]");
  if (body) body.innerHTML = renderTabPreferencesBody(draft, defaultTab, compact);
}

function commitSectionDraft(): TabPreferences | null {
  if (!mountedSection) return null;
  const prefs = draftToPreferences(mountedSection.draft, mountedSection.defaultTab);
  mountedSection.save(prefs);
  mountedSection.onApply(prefs);
  mountedSection.draft = buildDraft(mountedSection.definitions, prefs);
  mountedSection.defaultTab = prefs.defaultTab;
  const compact = mountedSection.host.classList.contains("sfe-tab-prefs-section--mobile");
  paintTabPreferencesBody(
    mountedSection.host,
    mountedSection.draft,
    mountedSection.defaultTab,
    compact,
  );
  return prefs;
}

export function commitMountedTabPreferences(): boolean {
  return commitSectionDraft() != null;
}

function reloadMountedSectionDraft(): void {
  if (!mountedSection) return;
  const load =
    mountedSection.platform === "web" ? loadWebTabPreferences : loadMobileTabPreferences;
  const current = load();
  mountedSection.draft = buildDraft(mountedSection.definitions, current);
  mountedSection.defaultTab = current.defaultTab;
  const compact = mountedSection.host.classList.contains("sfe-tab-prefs-section--mobile");
  paintTabPreferencesBody(
    mountedSection.host,
    mountedSection.draft,
    mountedSection.defaultTab,
    compact,
  );
}

/** Drop unsaved tab-pref edits (e.g. settings Cancel / close). */
export function discardMountedTabPreferencesDraft(): void {
  reloadMountedSectionDraft();
}

export function resetMountedTabPreferences(): boolean {
  if (!mountedSection) return false;
  const defaults = defaultPreferences(mountedSection.definitions);
  mountedSection.draft = buildDraft(mountedSection.definitions, defaults);
  mountedSection.defaultTab = defaults.defaultTab;
  const compact = mountedSection.host.classList.contains("sfe-tab-prefs-section--mobile");
  paintTabPreferencesBody(
    mountedSection.host,
    mountedSection.draft,
    mountedSection.defaultTab,
    compact,
  );
  return true;
}

function bindTabPreferencesHandlers(
  host: HTMLElement,
  compact: boolean,
  getDraft: () => { draft: TabPrefDraftEntry[]; defaultTab: string },
  setDraft: (draft: TabPrefDraftEntry[], defaultTab: string) => void,
  onApply: (prefs: TabPreferences) => void,
  definitions: TabPrefDefinition[],
  save: (prefs: TabPreferences) => void,
): void {
  host.onclick = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    const action = target.closest<HTMLElement>("[data-action]")?.dataset.action;
    let { draft, defaultTab } = getDraft();

    if (action === "tab-prefs-reset") {
      const defaults = defaultPreferences(definitions);
      draft = buildDraft(definitions, defaults);
      defaultTab = defaults.defaultTab;
      setDraft(draft, defaultTab);
      paintTabPreferencesBody(host, draft, defaultTab, compact);
      return;
    }
    if (action === "tab-prefs-apply") {
      const prefs = draftToPreferences(draft, defaultTab);
      save(prefs);
      onApply(prefs);
      draft = buildDraft(definitions, prefs);
      defaultTab = prefs.defaultTab;
      setDraft(draft, defaultTab);
      paintTabPreferencesBody(host, draft, defaultTab, compact);
      return;
    }
    if (action === "tab-up" || action === "tab-down") {
      const index = Number(target.closest<HTMLElement>("[data-index]")?.dataset.index);
      if (!Number.isFinite(index)) return;
      draft = moveDraftEntry(draft, index, action === "tab-up" ? "up" : "down");
      setDraft(draft, defaultTab);
      paintTabPreferencesList(host, draft, compact);
    }
  };

  host.onchange = (event) => {
    const target = event.target;
    let { draft, defaultTab } = getDraft();

    if (target instanceof HTMLSelectElement && target.matches("[data-tab-default]")) {
      defaultTab = target.value;
      setDraft(draft, defaultTab);
      return;
    }
    if (!(target instanceof HTMLInputElement)) return;
    const id = target.dataset.tabToggle;
    if (!id) return;

    const before = draft.find((tab) => tab.id === id)?.visible;
    draft = toggleDraftEntry(draft, id, target.checked);
    const after = draft.find((tab) => tab.id === id)?.visible;
    if (before === after) {
      target.checked = before ?? true;
      return;
    }

    defaultTab = syncDefaultTabSelect(host, draft, defaultTab);
    setDraft(draft, defaultTab);
  };
}

export function mountTabPreferencesSection(
  host: HTMLElement,
  options: {
    platform: "web" | "mobile";
    onApply: (prefs: TabPreferences) => void;
  },
): void {
  if (host.hasAttribute(SECTION_BOUND)) return;

  const definitions =
    options.platform === "web" ? WEB_TAB_PREF_DEFINITIONS : MOBILE_TAB_PREF_DEFINITIONS;
  const load = options.platform === "web" ? loadWebTabPreferences : loadMobileTabPreferences;
  const save = options.platform === "web" ? saveWebTabPreferences : saveMobileTabPreferences;
  const compact = options.platform === "mobile";
  const current = load();

  let draft = buildDraft(definitions, current);
  let defaultTab = current.defaultTab;

  host.setAttribute(SECTION_BOUND, "1");
  host.classList.add("sfe-tab-prefs-section");
  if (compact) host.classList.add("sfe-tab-prefs-section--mobile");

  host.innerHTML = `
    <h4 class="wheel-settings-section-title sfe-web-settings-card-title">Navigation tabs</h4>
    <p class="muted small sfe-column-settings-lead">Show, hide, and reorder top navigation tabs. Pick which tab opens on launch, or resume the last tab you used.</p>
    <div data-tab-preferences-body></div>`;

  mountedSection = {
    host,
    draft,
    defaultTab,
    definitions,
    platform: options.platform,
    save,
    onApply: options.onApply,
  };

  paintTabPreferencesBody(host, draft, defaultTab, compact);

  bindTabPreferencesHandlers(
    host,
    compact,
    () => ({ draft, defaultTab }),
    (nextDraft, nextDefaultTab) => {
      draft = nextDraft;
      defaultTab = nextDefaultTab;
      if (mountedSection?.host === host) {
        mountedSection.draft = nextDraft;
        mountedSection.defaultTab = nextDefaultTab;
      }
    },
    options.onApply,
    definitions,
    save,
  );
}

function moveDraftEntry(draft: TabPrefDraftEntry[], index: number, direction: "up" | "down"): TabPrefDraftEntry[] {
  const next = draft.map((tab) => ({ ...tab }));
  const target = direction === "up" ? index - 1 : index + 1;
  if (target < 0 || target >= next.length) return next;
  if (next[index]?.locked || next[target]?.locked) return next;
  const temp = next[index]!;
  next[index] = next[target]!;
  next[target] = temp;
  return next;
}

function toggleDraftEntry(draft: TabPrefDraftEntry[], id: string, visible: boolean): TabPrefDraftEntry[] {
  const next = draft.map((tab) => ({ ...tab }));
  const visibleCount = next.filter((tab) => tab.visible || tab.locked).length;
  return next.map((tab) => {
    if (tab.id !== id || tab.locked) return tab;
    if (!visible && visibleCount <= 1) return tab;
    return { ...tab, visible };
  });
}

function renderDraftBody(draft: TabPrefDraftEntry[], defaultTab: string): string {
  return renderTabPreferencesBody(draft, defaultTab, false);
}

export function openTabPreferencesModal(options: {
  platform: "web" | "mobile";
  onApply: (prefs: TabPreferences) => void;
}): void {
  activeDefinitions =
    options.platform === "web" ? WEB_TAB_PREF_DEFINITIONS : MOBILE_TAB_PREF_DEFINITIONS;
  activePlatform = options.platform;
  activeOnApply = options.onApply;

  const current =
    options.platform === "web" ? loadWebTabPreferences() : loadMobileTabPreferences();
  activeDefaultTab = current.defaultTab;
  activeDraft = buildDraft(activeDefinitions, current);

  let overlay = document.getElementById(MODAL_ID);

  const close = (): void => {
    overlay?.classList.remove("sfe-modal-open");
  };

  const paint = (): void => {
    const body = overlay?.querySelector<HTMLElement>("[data-tab-preferences-body]");
    if (body) body.innerHTML = renderDraftBody(activeDraft, activeDefaultTab);
  };

  const paintList = (): void => {
    const list = overlay?.querySelector<HTMLElement>("[data-tab-preferences-list]");
    if (!list) return;
    const scrollTop = list.scrollTop;
    list.innerHTML = renderTabPreferenceRows(activeDraft, false);
    list.scrollTop = scrollTop;
  };

  if (!overlay) {
    overlay = document.createElement("div");
    overlay.id = MODAL_ID;
    overlay.className = "sfe-modal-overlay";
    overlay.innerHTML = `
      <div class="sfe-modal sfe-column-settings-modal sfe-tab-preferences-modal" role="dialog" aria-labelledby="sfe-tab-preferences-title">
        <div class="sfe-modal-header">
          <h3 id="sfe-tab-preferences-title">Customize tabs</h3>
          <button type="button" class="sfe-modal-close" data-action="tab-prefs-close" aria-label="Close">✕</button>
        </div>
        <p class="muted small sfe-column-settings-lead">Show, hide, and reorder top navigation tabs. Pick which tab opens on launch, or resume the last tab you used.</p>
        <div data-tab-preferences-body></div>
        <div class="sfe-modal-actions sfe-column-settings-actions">
          <button type="button" class="ghost small" data-action="tab-prefs-reset">Reset</button>
          <button type="button" class="ghost small" data-action="tab-prefs-cancel">Cancel</button>
          <button type="button" class="small" data-action="tab-prefs-apply">Apply</button>
        </div>
      </div>`;
    document.body.appendChild(overlay);
  }

  if (!modalBound && overlay) {
    modalBound = true;
    overlay.addEventListener("click", (event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      if (target === overlay) {
        close();
        return;
      }
      const action = target.closest<HTMLElement>("[data-action]")?.dataset.action;
      if (action === "tab-prefs-close" || action === "tab-prefs-cancel") close();
      if (action === "tab-prefs-reset") {
        const defaults = defaultPreferences(activeDefinitions);
        activeDefaultTab = defaults.defaultTab;
        activeDraft = buildDraft(activeDefinitions, defaults);
        paint();
      }
      if (action === "tab-prefs-apply") {
        const prefs = draftToPreferences(activeDraft, activeDefaultTab);
        if (activePlatform === "web") saveWebTabPreferences(prefs);
        else saveMobileTabPreferences(prefs);
        activeOnApply?.(prefs);
        close();
      }
      if (action === "tab-up" || action === "tab-down") {
        const index = Number(target.closest<HTMLElement>("[data-index]")?.dataset.index);
        if (!Number.isFinite(index)) return;
        activeDraft = moveDraftEntry(activeDraft, index, action === "tab-up" ? "up" : "down");
        paintList();
      }
    });

    overlay.addEventListener("change", (event) => {
      const target = event.target;
      if (target instanceof HTMLSelectElement && target.matches("[data-tab-default]")) {
        activeDefaultTab = target.value;
        return;
      }
      if (!(target instanceof HTMLInputElement)) return;
      const id = target.dataset.tabToggle;
      if (!id) return;
      const before = activeDraft.find((tab) => tab.id === id)?.visible;
      activeDraft = toggleDraftEntry(activeDraft, id, target.checked);
      const after = activeDraft.find((tab) => tab.id === id)?.visible;
      if (before === after) {
        target.checked = before ?? true;
        return;
      }
      if (overlay) {
        activeDefaultTab = syncDefaultTabSelect(overlay, activeDraft, activeDefaultTab);
      }
    });
  }

  paint();
  overlay.classList.add("sfe-modal-open");
}
